import React, { useCallback, useEffect, useMemo } from 'react';
import { Link as RouterLink, useLocation } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import {
  Box,
  Breadcrumbs,
  Button,
  Container,
  GlobalStyles,
  Link,
  Paper,
  Stack,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Typography,
  alpha,
  useMediaQuery,
  useTheme,
} from '@mui/material';
import NavigateNextIcon from '@mui/icons-material/NavigateNext';
import PrintOutlinedIcon from '@mui/icons-material/PrintOutlined';
import SEO from '../SEO';
import { LEGAL_DOCS, isLegalPlaceholder, legalValues } from '../../config/legal';

/** t() + veri sorumlusu değerleri ({{companyTitle}} vb.) */
export const useLegalT = () => {
  const { t, i18n } = useTranslation();
  const vars = useMemo(() => legalValues(i18n.language), [i18n.language]);
  return useCallback((key, opts) => t(key, { ...vars, ...opts }), [t, vars]);
};

// [metin](/yol) bağlantı, **kalın**, [Doldurulacak alan] vurgusu
const TOKEN_RE = /(\[[^\]]+\]\([^)\s]+\)|\*\*[^*]+\*\*|\[[^\]]+\])/g;
const LINK_RE = /^\[([^\]]+)\]\(([^)\s]+)\)$/;
const BOLD_RE = /^\*\*([^*]+)\*\*$/;

const Placeholder = ({ children }) => (
  <Box
    component="mark"
    sx={(theme) => ({
      bgcolor: alpha(theme.palette.warning.main, 0.2),
      color: 'inherit',
      px: 0.5,
      borderRadius: 0.5,
    })}
  >
    {children}
  </Box>
);

/** Yasal metinlerdeki satır içi biçimlendirmeyi React öğelerine çevirir. */
export const LegalInline = ({ text, linkTarget }) => {
  if (typeof text !== 'string' || !text) return null;
  return text.split(TOKEN_RE).map((part, i) => {
    if (!part) return null;
    const link = part.match(LINK_RE);
    if (link) {
      const [, label, href] = link;
      if (href.startsWith('/')) {
        return (
          <Link
            key={i}
            component={RouterLink}
            to={href}
            target={linkTarget}
            rel={linkTarget === '_blank' ? 'noopener noreferrer' : undefined}
            underline="hover"
            sx={{ fontWeight: 600 }}
          >
            {label}
          </Link>
        );
      }
      const external = /^https?:/i.test(href);
      return (
        <Link
          key={i}
          href={href}
          target={external ? '_blank' : undefined}
          rel={external ? 'noopener noreferrer' : undefined}
          underline="hover"
          sx={{ fontWeight: 600, wordBreak: 'break-word' }}
        >
          {label}
        </Link>
      );
    }
    const bold = part.match(BOLD_RE);
    if (bold) return <strong key={i}>{bold[1]}</strong>;
    if (isLegalPlaceholder(part)) return <Placeholder key={i}>{part}</Placeholder>;
    return <React.Fragment key={i}>{part}</React.Fragment>;
  });
};

const CONTROLLER_FIELDS = ['companyTitle', 'mersisNo', 'tradeRegistryNo', 'taxOffice', 'taxNo', 'address', 'kepAddress', 'kvkkEmail', 'phone', 'verbisNo', 'website'];

const ControllerCard = () => {
  const tl = useLegalT();
  const { i18n } = useTranslation();
  const values = legalValues(i18n.language);
  const rows = CONTROLLER_FIELDS.filter((key) => values[key]);
  return (
    <Box
      component="dl"
      sx={(theme) => ({
        m: 0,
        mb: 2,
        p: { xs: 2, sm: 2.5 },
        borderRadius: 2,
        border: `1px solid ${theme.palette.divider}`,
        bgcolor: alpha(theme.palette.primary.main, theme.palette.mode === 'dark' ? 0.08 : 0.03),
        display: 'grid',
        gridTemplateColumns: { xs: '1fr', sm: 'minmax(160px, 220px) 1fr' },
        columnGap: 2,
        rowGap: { xs: 0.25, sm: 1 },
      })}
    >
      {rows.map((key) => {
        const value = values[key];
        let content = <LegalInline text={value} />;
        if (key === 'kvkkEmail' && !isLegalPlaceholder(value)) {
          content = <Link href={`mailto:${value}`} underline="hover">{value}</Link>;
        } else if (key === 'website') {
          content = <Link href={value} underline="hover">{value.replace(/^https?:\/\//, '')}</Link>;
        }
        return (
          <React.Fragment key={key}>
            <Typography component="dt" sx={{ fontSize: 13.5, color: 'text.secondary', mt: { xs: 1, sm: 0 } }}>
              {tl(`legal.common.controller.${key}`)}
            </Typography>
            <Typography component="dd" sx={{ m: 0, fontSize: 14.5, fontWeight: 550, wordBreak: 'break-word' }}>
              {content}
            </Typography>
          </React.Fragment>
        );
      })}
    </Box>
  );
};

const LegalTable = ({ head, rows }) => {
  const theme = useTheme();
  const stacked = useMediaQuery(theme.breakpoints.down('sm'));
  if (stacked) {
    return (
      <Stack spacing={1.25} sx={{ mb: 2.5 }}>
        {rows.map((row, r) => (
          <Box key={r} sx={{ p: 1.75, borderRadius: 1.5, border: `1px solid ${theme.palette.divider}` }}>
            {row.map((cell, c) => (
              <Box key={c} sx={{ mb: c < row.length - 1 ? 1 : 0 }}>
                <Typography sx={{ fontSize: 12, fontWeight: 700, color: 'text.secondary', textTransform: 'uppercase', letterSpacing: '0.03em' }}>
                  {head[c]}
                </Typography>
                <Typography sx={{ fontSize: 14, lineHeight: 1.6, fontWeight: c === 0 ? 600 : 400 }}>
                  <LegalInline text={cell} />
                </Typography>
              </Box>
            ))}
          </Box>
        ))}
      </Stack>
    );
  }
  return (
    <TableContainer
      component={Paper}
      variant="outlined"
      sx={{ mb: 2.5, borderRadius: 1.5, '@media print': { overflow: 'visible' } }}
    >
      <Table size="small">
        <TableHead>
          <TableRow sx={{ bgcolor: alpha(theme.palette.primary.main, 0.06) }}>
            {head.map((h, i) => (
              <TableCell key={i} sx={{ fontWeight: 700, fontSize: 13.5, verticalAlign: 'bottom' }}>
                {h}
              </TableCell>
            ))}
          </TableRow>
        </TableHead>
        <TableBody>
          {rows.map((row, r) => (
            <TableRow key={r} sx={{ '&:last-child td': { borderBottom: 0 } }}>
              {row.map((cell, c) => (
                <TableCell key={c} sx={{ fontSize: 14, lineHeight: 1.6, verticalAlign: 'top', fontWeight: c === 0 ? 600 : 400 }}>
                  <LegalInline text={cell} />
                </TableCell>
              ))}
            </TableRow>
          ))}
        </TableBody>
      </Table>
    </TableContainer>
  );
};

const LegalBlock = ({ block, slots }) => {
  if (typeof block === 'string') {
    return (
      <Typography paragraph sx={{ lineHeight: 1.75, color: 'text.primary' }}>
        <LegalInline text={block} />
      </Typography>
    );
  }
  if (!block || typeof block !== 'object') return null;
  switch (block.type) {
    case 'h3':
      return (
        <Typography component="h3" sx={{ fontWeight: 700, fontSize: '1.05rem', mt: 3, mb: 1.25 }}>
          {block.text}
        </Typography>
      );
    case 'list':
      return (
        <Box component="ul" sx={{ pl: 3, mt: 0, mb: 2, '& li': { mb: 0.85, lineHeight: 1.7 } }}>
          {(block.items || []).map((item, i) => (
            <li key={i}>
              <LegalInline text={item} />
            </li>
          ))}
        </Box>
      );
    case 'table':
      return <LegalTable head={block.head || []} rows={block.rows || []} />;
    case 'note':
      return (
        <Box
          sx={(theme) => ({
            mb: 2,
            p: 2,
            borderRadius: 1.5,
            borderLeft: `4px solid ${theme.palette.primary.main}`,
            bgcolor: alpha(theme.palette.primary.main, 0.06),
          })}
        >
          <Typography sx={{ lineHeight: 1.7 }}>
            <LegalInline text={block.text} />
          </Typography>
        </Box>
      );
    case 'controller':
      return <ControllerCard />;
    case 'slot':
      return slots?.[block.name] ? <Box sx={{ my: 2 }}>{slots[block.name]}</Box> : null;
    default:
      return null;
  }
};

const asArray = (v) => (Array.isArray(v) ? v : []);

/**
 * Ortak yasal sayfa düzeni: başlık, son güncelleme, içindekiler, bölümler, ilgili metinler, yazdırma.
 * @param ns      i18n kökü (ör. 'privacy', 'legal.kvkk')
 * @param docKey  LEGAL_DOCS anahtarı (ilgili metinler listesinde kendini gizlemek için)
 * @param slots   { [name]: ReactNode } — içerikteki {type:'slot'} blokları için
 */
const LegalPage = ({ ns, docKey, icon, slots }) => {
  const tl = useLegalT();
  const { i18n } = useTranslation();
  const theme = useTheme();
  const location = useLocation();

  const title = tl(`${ns}.title`);
  const intro = asArray(tl(`${ns}.intro`, { returnObjects: true }));
  const sections = asArray(tl(`${ns}.sections`, { returnObjects: true }));
  const translationNote = tl('legal.common.translationNote');

  // /kvkk#aktarim gibi bağlantılarda ilgili bölüme kaydır
  useEffect(() => {
    if (!location.hash) {
      window.scrollTo(0, 0);
      return undefined;
    }
    const id = decodeURIComponent(location.hash.slice(1));
    const timer = setTimeout(() => {
      const el = document.getElementById(id);
      if (el) el.scrollIntoView({ behavior: 'smooth', block: 'start' });
    }, 50);
    return () => clearTimeout(timer);
  }, [location.pathname, location.hash]);

  const toc = (
    <Box component="nav" aria-label={tl('legal.common.toc')}>
      <Typography sx={{ fontSize: 12.5, fontWeight: 700, letterSpacing: '0.06em', textTransform: 'uppercase', color: 'text.secondary', mb: 1.25 }}>
        {tl('legal.common.toc')}
      </Typography>
      <Box component="ol" sx={{ listStyle: 'none', m: 0, p: 0, '& li': { mb: 0.75 } }}>
        {sections.map((s) => (
          <li key={s.id}>
            <Link
              component={RouterLink}
              to={`${location.pathname}#${s.id}`}
              underline="hover"
              color="text.secondary"
              sx={{ fontSize: 14, lineHeight: 1.45, display: 'block', '&:hover': { color: 'primary.main' } }}
            >
              {s.title}
            </Link>
          </li>
        ))}
      </Box>
    </Box>
  );

  return (
    <>
      <SEO title={title} description={tl(`${ns}.description`)} />
      <GlobalStyles
        styles={{
          '@media print': {
            '.MuiAppBar-root, footer, [data-legal-noprint]': { display: 'none !important' },
            body: { background: '#fff !important' },
            a: { color: 'inherit !important' },
          },
        }}
      />

      <Box
        sx={{
          bgcolor: alpha(theme.palette.primary.main, theme.palette.mode === 'dark' ? 0.1 : 0.05),
          pt: { xs: 12, md: 14 },
          pb: { xs: 4, md: 5 },
          '@media print': { pt: 0, bgcolor: 'transparent' },
        }}
      >
        <Container maxWidth="lg">
          <Breadcrumbs separator={<NavigateNextIcon fontSize="small" />} sx={{ mb: 2.5 }} data-legal-noprint>
            <Link component={RouterLink} to="/" color="inherit" underline="hover">
              {tl('navigation.home')}
            </Link>
            <Typography color="text.primary">{title}</Typography>
          </Breadcrumbs>

          <Stack direction="row" spacing={2} alignItems="center" sx={{ mb: 1.5 }}>
            {icon ? (
              <Box sx={{ color: 'primary.main', display: 'flex', '& svg': { fontSize: { xs: 32, md: 40 } } }}>{icon}</Box>
            ) : null}
            <Typography component="h1" sx={{ fontWeight: 750, fontSize: { xs: '1.75rem', md: '2.4rem' }, lineHeight: 1.2, letterSpacing: '-0.02em' }}>
              {title}
            </Typography>
          </Stack>

          <Stack direction={{ xs: 'column', sm: 'row' }} spacing={{ xs: 1, sm: 2 }} alignItems={{ xs: 'flex-start', sm: 'center' }} sx={{ mb: 2.5 }}>
            <Typography color="text.secondary" sx={{ fontSize: 14.5 }}>
              {tl('legal.common.lastUpdated')}
            </Typography>
            <Button
              size="small"
              variant="text"
              startIcon={<PrintOutlinedIcon fontSize="small" />}
              onClick={() => window.print()}
              data-legal-noprint
              sx={{ textTransform: 'none', px: 1 }}
            >
              {tl('legal.common.print')}
            </Button>
          </Stack>

          <Box sx={{ maxWidth: 820 }}>
            {intro.map((p, i) => (
              <Typography key={i} sx={{ color: 'text.secondary', lineHeight: 1.75, mb: 1.25, fontSize: { xs: 15, md: 16 } }}>
                <LegalInline text={p} />
              </Typography>
            ))}
            {String(i18n.language || '').startsWith('en') && translationNote ? (
              <Typography sx={{ color: 'text.secondary', fontSize: 13.5, fontStyle: 'italic', mt: 1 }}>{translationNote}</Typography>
            ) : null}
          </Box>
        </Container>
      </Box>

      <Container maxWidth="lg" sx={{ py: { xs: 4, md: 6 } }}>
        <Box sx={{ display: 'grid', gridTemplateColumns: { xs: '1fr', md: '260px minmax(0, 1fr)' }, gap: { xs: 3, md: 5 } }}>
          <Box data-legal-noprint sx={{ display: { xs: 'none', md: 'block' } }}>
            <Box sx={{ position: 'sticky', top: 104, maxHeight: 'calc(100vh - 128px)', overflowY: 'auto', pr: 1 }}>{toc}</Box>
          </Box>
          <Paper data-legal-noprint variant="outlined" sx={{ display: { xs: 'block', md: 'none' }, p: 2, borderRadius: 2 }}>
            {toc}
          </Paper>

          <Box component="article" sx={{ minWidth: 0 }}>
            {sections.map((section) => (
              <Box component="section" key={section.id} sx={{ mb: 5 }}>
                <Typography
                  id={section.id}
                  component="h2"
                  sx={{ fontWeight: 700, fontSize: { xs: '1.2rem', md: '1.35rem' }, mb: 2, scrollMarginTop: '104px', color: 'text.primary' }}
                >
                  {section.title}
                </Typography>
                {asArray(section.blocks).map((block, i) => (
                  <LegalBlock key={i} block={block} slots={slots} />
                ))}
              </Box>
            ))}

            <Box data-legal-noprint sx={{ mt: 6, pt: 3, borderTop: `1px solid ${theme.palette.divider}` }}>
              <Typography sx={{ fontWeight: 700, mb: 1.5 }}>{tl('legal.common.related')}</Typography>
              <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 1 }}>
                {LEGAL_DOCS.filter((d) => d.key !== docKey).map((d) => (
                  <Button
                    key={d.key}
                    component={RouterLink}
                    to={d.path}
                    variant="outlined"
                    size="small"
                    sx={{ textTransform: 'none', borderRadius: 999 }}
                  >
                    {tl(`legal.common.docs.${d.key}`)}
                  </Button>
                ))}
              </Box>
            </Box>
          </Box>
        </Box>
      </Container>
    </>
  );
};

export default LegalPage;
